import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { shippingApi } from '../api/shippingApi';
import { buildPeriodApiFilters, fiscalPeriodInfo } from '../utils/fiscalPeriod';
import { resetFiltersToDefault } from '../utils/filterReset';
import {
  SUPPLIER_SHARE_DEFAULT_FILTERS,
  buildSupplierShareEditQueryFromViewFilters,
  buildSupplierShareFiltersFromSearchParams,
} from '../constants/supplierShareFilters';
import { HacchuSplitPlanInlineEditor } from '../features/shipping/HacchuSplitPlanInlineEditor';
import { patchItemsSplitPlans, SPLIT_PLAN_LATEST_FIELDS } from '../features/shipping/splitPlanUtils';
import { SupplierStockEditor } from '../features/shipping/SupplierStockEditor';
import { ShipmentOriginCell } from '../features/shipping/ShipmentOriginCell';
import {
  buildSupplierShareColumns,
  buildSupplierSharePrintColumns,
} from '../features/shipping/supplierShareColumnDefs';
import { todayShipRowClassName, isTodayShipShareRow } from '../features/shipping/todayShipShare';
import { ShipRequestIssueModal } from '../features/shipping/ShipRequestIssueModal';
import { ShipRequestListPanel } from '../features/shipping/ShipRequestListPanel';
import { ShipmentClusterEditModal } from '../features/shipping/ShipmentClusterEditModal';
import {
  patchDateFields,
  patchDestinationFields,
} from '../features/shipping/shipmentClusterUtils';
import { allowsArrivalDateInput } from '../features/shipping/shukkaDateRules';
import {
  PRINT_PAGE_TITLE_COUNT,
  PRINT_PAGE_TITLE_H1,
  PRINT_PAGE_TITLE_ROW,
  PRINT_SCROLL_CONTAINER,
  PRINT_SEARCH_ROW_TH,
  PRINT_SEARCH_ROW_TR,
  PRINT_TABLE,
  PRINT_TD_COMPOSITE,
  PRINT_TD_LEFT,
  PRINT_TD_NUM,
  PRINT_THEAD,
  PRINT_TH_LEFT,
  PRINT_TH_RIGHT,
  PRINT_TR_BODY,
  SUPPLIER_SHARE_PRINT_COL_WIDTHS,
  SUPPLIER_SHARE_PRINT_COMPOSITE_KEYS,
} from '../constants/printTableStyles';
import { PageSyncMeta } from '../components/sync/PageSyncMeta';
import { useSyncStatus } from '../contexts/SyncStatusContext';

// 仕入先コードマッピング
const SUPPLIER_CODES = {
  takara: 1576,  // 宝機材
  kunieda: 1878, // 国枝工業
  daikure: 2146, // ダイクレ
};

const SupplierSharePage = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const fiscalMeta = useMemo(() => fiscalPeriodInfo(), []);

  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const [expandedRows, setExpandedRows] = useState(new Set());
  const [loadError, setLoadError] = useState(null);
  const [requestFormHacchuNo, setRequestFormHacchuNo] = useState(null);
  const [clusterEditRow, setClusterEditRow] = useState(null);
  const [listMode, setListMode] = useState('orders'); // orders | requests
  const { refreshSyncStatus } = useSyncStatus();
  const offsetRef = useRef(0);
  const observerTarget = useRef(null);
  const observerRef = useRef(null);
  
  // タブ状態（リロードしても維持したいのでlocalStorageから初期化）
  const [activeTab, setActiveTab] = useState(() => {
    if (typeof window === 'undefined') return 'all';
    return window.localStorage.getItem('supplierShare.activeTab') || 'all';
  });
  // 宝機材タブ内のサブタブ（発注明細 / 在庫入力）
  const [takaraSubTab, setTakaraSubTab] = useState(() => {
    if (typeof window === 'undefined') return 'details';
    return window.localStorage.getItem('supplierShare.takaraSubTab') || 'details';
  }); // 'details' | 'stock'
  // 製品マスタ情報マップ（product_disp -> { product_name, product_size, tani }）
  const [productMasterMap, setProductMasterMap] = useState({});
  
  // フィルタ状態（URLパラメータから初期化）
  const initialFilters = buildSupplierShareFiltersFromSearchParams(searchParams);
  const [filters, setFilters] = useState(initialFilters);
  const [appliedFilters, setAppliedFilters] = useState(initialFilters);
  const isPrintView = searchParams.get('print_view') === '1';
  const PRINT_MAX_COUNT = 1000;
  const PRINT_WARNING_THRESHOLD = 200;
  
  // 最新の状態を参照するためのref
  const stateRef = useRef({ loadingMore: false, hasMore: true, loading: true });
  const activeTabRef = useRef(activeTab);
  const appliedFiltersRef = useRef(appliedFilters);
  
  useEffect(() => {
    stateRef.current = { loadingMore, hasMore, loading };
  }, [loadingMore, hasMore, loading]);

  useEffect(() => {
    activeTabRef.current = activeTab;
  }, [activeTab]);

  useEffect(() => {
    appliedFiltersRef.current = appliedFilters;
  }, [appliedFilters]);

  // 印刷用ビュー表示時は body にクラスを付与し、印刷時に親のスクロール制約を解除する
  useEffect(() => {
    if (isPrintView) {
      document.body.classList.add('shipping-print-view');
    }
    return () => {
      document.body.classList.remove('shipping-print-view');
    };
  }, [isPrintView]);

  // URLパラメータを更新
  const updateSearchParams = (newFilters) => {
    const params = new URLSearchParams();
    if (newFilters.productQuery) {
      params.set('product_query', newFilters.productQuery);
    }
    if (newFilters.customerName) {
      params.set('customer_name', newFilters.customerName);
    }
    if (newFilters.startDate) {
      params.set('start_date', newFilters.startDate);
    }
    if (newFilters.endDate) {
      params.set('end_date', newFilters.endDate);
    }
    if (newFilters.supplierResponseDueDateNullOnly) {
      params.set('supplier_response_due_date_null_only', 'true');
    }
    if (newFilters.supplierResponseDueDateSetOnly) {
      params.set('supplier_response_due_date_set_only', 'true');
    }
    if (newFilters.shipRequestUnissuedOnly) {
      params.set('ship_request_unissued_only', 'true');
    }
    if (newFilters.shipRequestIssuedOnly) {
      params.set('ship_request_issued_only', 'true');
    }
    if (newFilters.hacchuDateFromToday) {
      params.set('hacchu_date_from_today', 'true');
    }
    // 期のフィルタ
    if (newFilters.currentPeriodOnly) {
      params.set('current_period_only', 'true');
    } else if (newFilters.previousPeriodsOnly) {
      params.set('previous_periods_only', 'true');
    }
    // 受枠フィルタ（全体タブのみ）
    if (newFilters.jukuwakuFilter) {
      params.set('jukuwaku_filter', 'true');
    }
    if (newFilters.ogakiFactoryOnly) {
      params.set('ogaki_factory_only', 'true');
    }
    if (newFilters.todayShipOnly) {
      params.set('today_ship_only', 'true');
    }
    // 両方falseの場合はパラメータに含めない（全期間表示）
    setSearchParams(params, { replace: true });
  };

  const loadInitialItems = useCallback(async () => {
    try {
      setLoading(true);
      setLoadError(null);
      offsetRef.current = 0;
      // 印刷用ビューでは印刷上限件数まで一括取得し、それ以外は無限スクロール用に100件ずつ取得
      const initialLimit = isPrintView ? PRINT_MAX_COUNT : 100;
      // タブに応じてfilterSupplierCodeとexcludeSupplierCodesを設定
      let filtersWithTab = buildPeriodApiFilters(appliedFilters);

      if (activeTab === 'kunieda') {
        filtersWithTab.filterSupplierCode = 1878;
        filtersWithTab.excludeSupplierCodes = null;
      } else if (activeTab === 'takara') {
        filtersWithTab.filterSupplierCode = 1576;
        filtersWithTab.excludeSupplierCodes = null;
      } else if (activeTab === 'daikure') {
        filtersWithTab.filterSupplierCode = 2146;
        filtersWithTab.excludeSupplierCodes = null;
      } else if (activeTab === 'others') {
        filtersWithTab.filterSupplierCode = null;
        filtersWithTab.excludeSupplierCodes = [1878, 1576, 2146];
      } else {
        // 全体タブ
        filtersWithTab.filterSupplierCode = null;
        filtersWithTab.excludeSupplierCodes = null;
        // 受枠フィルタ（全体タブのみ）
        if (appliedFilters.jukuwakuFilter) {
          filtersWithTab.classcode0 = 16; // 受枠のclasscode0
        } else {
          filtersWithTab.classcode0 = null;
        }
      }
      // 大垣工場フィルタは宝機材タブでのみ適用
      if (activeTab !== 'takara') {
        filtersWithTab.ogakiFactoryOnly = false;
      }

      let data = await shippingApi.getSupplierShareList(initialLimit, 0, filtersWithTab);
      data = data.data || data;
      const originalDataLength = data.length; // フィルタリング前の件数を保持
      
      // 発注日起点のフィルタリング（今日以降）
      if (appliedFilters.hacchuDateFromToday) {
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        
        // 発注日文字列をパースするヘルパー関数
        const parseHacchuDate = (dateStr) => {
          if (!dateStr || dateStr === '-') return null;
          
          // "24/01/15(月)" 形式の場合
          const match = dateStr.match(/^(\d{2})\/(\d{2})\/(\d{2})/);
          if (match) {
            const year = 2000 + parseInt(match[1], 10);
            const month = parseInt(match[2], 10) - 1;
            const day = parseInt(match[3], 10);
            return new Date(year, month, day);
          }
          
          // ISO形式やその他の形式を試す
          const date = new Date(dateStr);
          if (!isNaN(date.getTime())) {
            return date;
          }
          
          return null;
        };
        
        data = data.filter(item => {
          const hacchuDate = parseHacchuDate(item.hacchu_date);
          if (!hacchuDate) {
            return false; // 発注日がない場合は除外
          }
          
          hacchuDate.setHours(0, 0, 0, 0);
          return hacchuDate >= today;
        });
        
        // 発注日が古い順にソート
        data.sort((a, b) => {
          const dateA = parseHacchuDate(a.hacchu_date);
          const dateB = parseHacchuDate(b.hacchu_date);
          if (!dateA && !dateB) return 0;
          if (!dateA) return 1;
          if (!dateB) return -1;
          return dateA - dateB; // 古い順（昇順）
        });
      }
      
      setItems(data);
      // 仕入先共有一覧から製品マスタ情報を構築
      setProductMasterMap(prev => {
        const map = { ...prev };
        data.forEach(item => {
          if (!item.product_disp) return;
          if (!map[item.product_disp]) {
            map[item.product_disp] = {
              product_name: item.product_name || '-',
              product_size: item.product_size || '-',
              tani: item.tani || '-',
            };
          }
        });
        return map;
      });
      // offsetRefはバックエンドから取得した累積件数（フィルタリング前）で更新
      offsetRef.current = originalDataLength;
      // hasMoreはバックエンドから取得した件数（フィルタリング前）で判定
      setHasMore(originalDataLength === 100);
    } catch (error) {
      console.error('仕入先共有一覧取得エラー:', error);
      setLoadError('仕入先共有一覧の取得に失敗しました');
      alert('仕入先共有一覧の取得に失敗しました');
    } finally {
      setLoading(false);
    }
  }, [appliedFilters, activeTab, isPrintView]);

  useEffect(() => {
    loadInitialItems();
  }, [loadInitialItems]);

  const handleRefresh = async () => {
    await Promise.all([loadInitialItems(), refreshSyncStatus()]);
  };

  const loadMoreItems = async () => {
    const currentState = stateRef.current;
    if (currentState.loadingMore || !currentState.hasMore || currentState.loading) {
      return;
    }

    try {
      const currentOffset = offsetRef.current;
      const currentActiveTab = activeTabRef.current;
      const currentAppliedFilters = appliedFiltersRef.current;
      
      setLoadingMore(true);
      // タブに応じてfilterSupplierCodeとexcludeSupplierCodesを設定
      let filtersWithTab = buildPeriodApiFilters(currentAppliedFilters);

      if (currentActiveTab === 'kunieda') {
        filtersWithTab.filterSupplierCode = 1878;
        filtersWithTab.excludeSupplierCodes = null;
      } else if (currentActiveTab === 'takara') {
        filtersWithTab.filterSupplierCode = 1576;
        filtersWithTab.excludeSupplierCodes = null;
      } else if (currentActiveTab === 'daikure') {
        filtersWithTab.filterSupplierCode = 2146;
        filtersWithTab.excludeSupplierCodes = null;
      } else if (currentActiveTab === 'others') {
        filtersWithTab.filterSupplierCode = null;
        filtersWithTab.excludeSupplierCodes = [1878, 1576, 2146];
      } else {
        // 全体タブ
        filtersWithTab.filterSupplierCode = null;
        filtersWithTab.excludeSupplierCodes = null;
        // 受枠フィルタ（全体タブのみ）
        if (currentAppliedFilters.jukuwakuFilter) {
          filtersWithTab.classcode0 = 16; // 受枠のclasscode0
        } else {
          filtersWithTab.classcode0 = null;
        }
      }
      // 大垣工場フィルタは宝機材タブでのみ適用
      if (currentActiveTab !== 'takara') {
        filtersWithTab.ogakiFactoryOnly = false;
      }

      let data = await shippingApi.getSupplierShareList(100, currentOffset, filtersWithTab);
      data = data.data || data;
      const originalDataLength = data.length; // フィルタリング前の件数を保持
      
      // 発注日起点のフィルタリング（今日以降）
      if (currentAppliedFilters.hacchuDateFromToday) {
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        
        // 発注日文字列をパースするヘルパー関数
        const parseHacchuDate = (dateStr) => {
          if (!dateStr || dateStr === '-') return null;
          
          // "24/01/15(月)" 形式の場合
          const match = dateStr.match(/^(\d{2})\/(\d{2})\/(\d{2})/);
          if (match) {
            const year = 2000 + parseInt(match[1], 10);
            const month = parseInt(match[2], 10) - 1;
            const day = parseInt(match[3], 10);
            return new Date(year, month, day);
          }
          
          // ISO形式やその他の形式を試す
          const date = new Date(dateStr);
          if (!isNaN(date.getTime())) {
            return date;
          }
          
          return null;
        };
        
        data = data.filter(item => {
          const hacchuDate = parseHacchuDate(item.hacchu_date);
          if (!hacchuDate) {
            return false; // 発注日がない場合は除外
          }
          
          hacchuDate.setHours(0, 0, 0, 0);
          return hacchuDate >= today;
        });
      }
      
      // フィルタリング後のデータが0件の場合、次のAPI呼び出しをスキップするために
      // offsetRefを更新して、hasMoreをfalseにする
      if (data.length === 0) {
        // フィルタリング後のデータが0件の場合でも、バックエンドから取得した件数でoffsetを更新
        offsetRef.current = currentOffset + originalDataLength;
        // バックエンドから取得した件数が100件未満の場合は、これ以上データがない
        if (originalDataLength < 100) {
          setHasMore(false);
        }
        // データが0件の場合は追加しない
      } else {
        setItems(prevItems => [...prevItems, ...data]);
        // 追加データから製品マスタ情報を更新
        setProductMasterMap(prev => {
          const map = { ...prev };
          data.forEach(item => {
            if (!item.product_disp) return;
            if (!map[item.product_disp]) {
              map[item.product_disp] = {
                product_name: item.product_name || '-',
                product_size: item.product_size || '-',
                tani: item.tani || '-',
              };
            }
          });
          return map;
        });
        // offsetRefはフィルタリング前の件数で更新（バックエンドから取得した件数）
        offsetRef.current = currentOffset + originalDataLength;
        // hasMoreはバックエンドから取得した件数（フィルタリング前）で判定
        setHasMore(originalDataLength === 100);
      }
    } catch (error) {
      console.error('追加データ取得エラー:', error);
    } finally {
      setLoadingMore(false);
    }
  };

  useEffect(() => {
    // items.lengthが0の場合は、observerTargetが存在しないのでスキップ
    if (items.length === 0) {
      return;
    }
    
    // オブザーバーは一度だけ作成
    if (!observerRef.current) {
      observerRef.current = new IntersectionObserver(
        (entries) => {
          const entry = entries[0];
          if (!entry) return;

          const currentState = stateRef.current;
          
          if (entry.isIntersecting && currentState.hasMore && !currentState.loadingMore && !currentState.loading) {
            loadMoreItems();
          }
        },
        { 
          threshold: 0,
          rootMargin: '500px'
        }
      );
    }

    const observer = observerRef.current;

    // observerTargetが存在するまで待機してからオブザーバーを設定
    const setupObserver = () => {
      const target = observerTarget.current;
      if (target) {
        // 既に監視中の場合は一度解除してから再設定（確実に監視を開始するため）
        const isObserving = target.getAttribute('data-observing') === 'true';
        if (isObserving) {
          observer.unobserve(target);
          target.removeAttribute('data-observing');
        }
        // 少し待ってから再設定（DOM更新を待つ）
        const timeoutId = setTimeout(() => {
          const currentTarget = observerTarget.current;
          if (currentTarget && !currentTarget.getAttribute('data-observing')) {
            observer.observe(currentTarget);
            currentTarget.setAttribute('data-observing', 'true');
          }
        }, 50);
        return { timeoutId };
      }
      return null;
    };

    // 即座にチェック
    let setupResult = setupObserver();
    let timeoutId1 = null;
    let timeoutId2 = null;
    let timeoutId3 = null;

    if (!setupResult) {
      // 少し待ってから再試行（DOMが更新されるのを待つ）
      timeoutId1 = setTimeout(() => {
        setupResult = setupObserver();
        if (!setupResult) {
          // もう一度試行
          timeoutId2 = setTimeout(() => {
            setupResult = setupObserver();
            if (!setupResult) {
              // 最後の試行
              timeoutId3 = setTimeout(() => {
                setupObserver();
              }, 300);
            }
          }, 300);
        }
      }, 300);
    }

    return () => {
      if (setupResult?.timeoutId) clearTimeout(setupResult.timeoutId);
      if (timeoutId1) clearTimeout(timeoutId1);
      if (timeoutId2) clearTimeout(timeoutId2);
      if (timeoutId3) clearTimeout(timeoutId3);
      const target = observerTarget.current;
      if (target && target.hasAttribute('data-observing')) {
        observer.unobserve(target);
        target.removeAttribute('data-observing');
      }
    };
  }, [hasMore, items.length]); // hasMoreとitems.lengthを依存配列に追加

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    const date = new Date(dateStr);
    if (isNaN(date.getTime())) return '';
    return date.toISOString().split('T')[0];
  };

  const formatDateDisplay = (dateStr) => {
    if (!dateStr) return '-';
    const date = new Date(dateStr);
    return date.toLocaleDateString('ja-JP', { year: 'numeric', month: '2-digit', day: '2-digit' });
  };

  const formatNumber = (num) => {
    if (num === null || num === undefined) return '-';
    return num.toLocaleString();
  };

  const formatPrice = (price) => {
    if (price === null || price === undefined) return '-';
    return price.toLocaleString('ja-JP', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  };

  const getItemKey = (item) => {
    return `${item.hacchu_code}-${item.hacchu_changecnt}-${item.hacchu_datacode}`;
  };

  const handleRowClick = (item, e) => {
    // 入力フィールドのクリックは展開をトリガーしない
    if (e.target.tagName === 'INPUT' || e.target.closest('input')) {
      return;
    }
    
    const key = getItemKey(item);
    const newExpandedRows = new Set(expandedRows);
    
    if (newExpandedRows.has(key)) {
      // 既に開いている場合は閉じる
      newExpandedRows.delete(key);
    } else {
      // 新しい行を開く場合は、既存の展開をすべて閉じてから開く
      newExpandedRows.clear();
      newExpandedRows.add(key);
    }
    
    setExpandedRows(newExpandedRows);
  };

  const handleSplitPlanDataChange = useCallback((rowId, splitPlans) => {
    setItems((prevItems) =>
      patchItemsSplitPlans(prevItems, rowId, getItemKey, splitPlans, SPLIT_PLAN_LATEST_FIELDS)
    );
  }, []);

  const handleFilterChange = (field, value) => {
    const newFilters = { ...filters, [field]: value };
    
    // 予定納期フィルタの排他制御
    if (field === 'supplierResponseDueDateNullOnly' && value === true) {
      newFilters.supplierResponseDueDateSetOnly = false;
    } else if (field === 'supplierResponseDueDateSetOnly' && value === true) {
      newFilters.supplierResponseDueDateNullOnly = false;
    }

    // 依頼書発行状態フィルタの排他制御
    if (field === 'shipRequestUnissuedOnly' && value === true) {
      newFilters.shipRequestIssuedOnly = false;
    } else if (field === 'shipRequestIssuedOnly' && value === true) {
      newFilters.shipRequestUnissuedOnly = false;
    }
    
    setFilters(newFilters);
    // 検索ボタンクリック時のみ適用（発送予定ページと同様）
  };

  const handleFilterClear = (field) => {
    const newFilters = { ...filters, [field]: typeof filters[field] === 'boolean' ? false : '' };
    setFilters(newFilters);
    // 検索ボタンクリック時のみ適用（発送予定ページと同様）
  };

  const handleAllFiltersClear = () => {
    resetFiltersToDefault({
      defaults: SUPPLIER_SHARE_DEFAULT_FILTERS,
      setFilters,
      setAppliedFilters,
      updateSearchParams,
    });
  };

  const handleFilterSubmit = (e) => {
    e.preventDefault();
    setAppliedFilters(filters);
    updateSearchParams(filters);
    // 検索実行（loadInitialItemsはuseEffectで自動実行される）
  };

  const handleTabChange = (tab) => {
    setActiveTab(tab);
    try {
      if (typeof window !== 'undefined') {
        window.localStorage.setItem('supplierShare.activeTab', tab);
      }
    } catch (e) {
      console.warn('Failed to persist activeTab to localStorage', e);
    }
    setExpandedRows(new Set()); // タブ切り替え時に展開をリセット
    setHasMore(true); // タブ切り替え時にhasMoreをリセット
    // 宝機材以外に切り替えたらサブタブをリセット
    if (tab !== 'takara') {
      setTakaraSubTab('details');
      try {
        if (typeof window !== 'undefined') {
          window.localStorage.setItem('supplierShare.takaraSubTab', 'details');
        }
      } catch (e) {
        console.warn('Failed to persist takaraSubTab to localStorage', e);
      }
    }
  };

  // 発送元更新（ダイクレ専用）
  const handleShipmentOriginUpdate = async (row, origin) => {
    try {
      await shippingApi.updateShipmentPlan(
        row.hacchu_code,
        row.hacchu_changecnt,
        row.hacchu_datacode,
        { shipment_origin: origin }
      );
      // ローカル状態を更新
      setItems(prev => prev.map(item =>
        item.hacchu_code === row.hacchu_code &&
        item.hacchu_changecnt === row.hacchu_changecnt &&
        item.hacchu_datacode === row.hacchu_datacode
          ? { ...item, shipment_origin: origin }
          : item
      ));
    } catch (error) {
      console.error('発送元更新エラー:', error);
      alert('発送元の更新に失敗しました');
    }
  };

  const patchLocalItem = (row, patch) => {
    setItems((prev) =>
      prev.map((item) =>
        item.hacchu_code === row.hacchu_code &&
        item.hacchu_changecnt === row.hacchu_changecnt &&
        item.hacchu_datacode === row.hacchu_datacode
          ? { ...item, ...patch }
          : item
      )
    );
  };

  const handleShipmentDateChange = async (row, value) => {
    try {
      await shippingApi.updateShipmentPlan(
        row.hacchu_code,
        row.hacchu_changecnt,
        row.hacchu_datacode,
        { shipment_date: value }
      );
      const patch = {
        shipment_date: value,
        latest_shipment_date: value,
      };
      if (!allowsArrivalDateInput(row.shukka_type)) {
        patch.arrival_date = null;
        patch.latest_arrival_date = null;
      }
      patchLocalItem(row, patch);
    } catch (error) {
      console.error('発送日更新エラー:', error);
      alert(error?.response?.data?.detail || '発送日の更新に失敗しました');
    }
  };

  const handleArrivalDateChange = async (row, value) => {
    if (!allowsArrivalDateInput(row.shukka_type)) {
      return;
    }
    try {
      await shippingApi.updateShipmentPlan(
        row.hacchu_code,
        row.hacchu_changecnt,
        row.hacchu_datacode,
        { arrival_date: value }
      );
      patchLocalItem(row, {
        arrival_date: value,
        latest_arrival_date: value,
      });
    } catch (error) {
      console.error('着日更新エラー:', error);
      alert(error?.response?.data?.detail || '着日の更新に失敗しました');
    }
  };

  const handleClusterEditSaved = useCallback(({ reset, anchorRow, payload, applyFields }) => {
    if (reset || !anchorRow) {
      loadInitialItems();
      return;
    }
    setItems((prev) => {
      let next = prev;
      const destFields = {};
      if (applyFields?.includes('direct_name')) destFields.juchu_direct_name = payload.direct_name;
      if (applyFields?.includes('direct_address') || applyFields?.includes('postal_code')) {
        const postal = payload.postal_code ? `〒${payload.postal_code} ` : '';
        const body = payload.direct_address || '';
        destFields.juchu_direct_address = `${postal}${body}`.trim();
      }
      if (applyFields?.includes('direct_tel')) destFields.juchu_direct_tel = payload.direct_tel;
      if (applyFields?.includes('biko')) destFields.juchu_biko = payload.biko;
      if (applyFields?.includes('share_memo')) destFields.supplier_share_memo = payload.share_memo;
      if (Object.keys(destFields).length > 0) {
        next = patchDestinationFields(next, anchorRow, destFields);
      }
      const dateFields = {};
      if (applyFields?.includes('shipment_date')) dateFields.shipment_date = payload.shipment_date;
      if (applyFields?.includes('arrival_date')) dateFields.arrival_date = payload.arrival_date;
      if (Object.keys(dateFields).length > 0 && payload.selected_date_targets) {
        next = patchDateFields(next, payload.selected_date_targets, dateFields);
        if (applyFields?.includes('shipment_date')) {
          const targetKeys = new Set(
            payload.selected_date_targets.map(
              (t) => `${t.hacchu_code}:${t.hacchu_datacode}`
            )
          );
          next = next.map((row) => {
            const key = `${row.hacchu_code}:${row.hacchu_datacode}`;
            if (!targetKeys.has(key) || allowsArrivalDateInput(row.shukka_type)) {
              return row;
            }
            return {
              ...row,
              arrival_date: null,
              latest_arrival_date: null,
            };
          });
        }
      }
      return next;
    });
    if (anchorRow?.latest_ship_request_no || anchorRow?.ship_request_issue_status === 'full' || anchorRow?.ship_request_issue_status === 'partial') {
      window.alert(
        '発行済みの依頼があります。送り先等を変えた場合は、必要なら再発行してください。'
      );
    }
  }, [loadInitialItems]);

  const columns = useMemo(
    () =>
      buildSupplierShareColumns('view', {
        formatDate,
        formatNumber,
        onShipmentDateChange: handleShipmentDateChange,
        onArrivalDateChange: handleArrivalDateChange,
        onHacchuNoClick: (hacchuNo) => setRequestFormHacchuNo(hacchuNo),
        onClusterEdit: (row) => setClusterEditRow(row),
        includeStock: true,
        includeShipmentOrigin: activeTab === 'daikure',
        StockCell: ({ row }) => {
          const isStock = !!row.supplier_stock_flag;
          return (
            <span
              className={`inline-flex items-center px-2 py-1 text-xs rounded-full border ${
                isStock
                  ? 'bg-emerald-50 border-emerald-500 text-emerald-700'
                  : 'bg-white border-slate-300 text-slate-400'
              }`}
            >
              {isStock ? '在庫' : '-'}
            </span>
          );
        },
        ShipmentOriginCell: ({ row }) => (
          <ShipmentOriginCell
            value={row.shipment_origin || null}
            onChange={(origin) => handleShipmentOriginUpdate(row, origin)}
          />
        ),
      }),
    [
      activeTab,
      handleShipmentDateChange,
      handleArrivalDateChange,
      handleShipmentOriginUpdate,
    ]
  );

  const getActiveTabLabel = () => {
    if (activeTab === 'all') return '全体';
    if (activeTab === 'kunieda') return '国枝工業';
    if (activeTab === 'takara') return '宝機材';
    if (activeTab === 'daikure') return 'ダイクレ';
    if (activeTab === 'others') return 'その他';
    return activeTab;
  };

  const getPrintSearchSummary = () => {
    const parts = [];
    parts.push(`タブ=${getActiveTabLabel()}`);
    if (appliedFilters.productQuery) parts.push(`製品・寸法=${appliedFilters.productQuery}`);
    if (appliedFilters.customerName) parts.push(`得意先=${appliedFilters.customerName}`);
    if (appliedFilters.startDate) parts.push(`開始日=${appliedFilters.startDate}`);
    if (appliedFilters.endDate) parts.push(`終了日=${appliedFilters.endDate}`);
    if (appliedFilters.currentPeriodOnly) parts.push('今期のみ');
    if (appliedFilters.previousPeriodsOnly) parts.push('前期以前');
    if (appliedFilters.hacchuDateFromToday) parts.push('発注日今日以降');
    if (appliedFilters.supplierResponseDueDateNullOnly) parts.push('予定納期未設定');
    if (appliedFilters.supplierResponseDueDateSetOnly) parts.push('予定納期設定済');
    if (appliedFilters.shipRequestUnissuedOnly) parts.push('未発行');
    if (appliedFilters.shipRequestIssuedOnly) parts.push('発行済');
    if (appliedFilters.jukuwakuFilter) parts.push('受枠');
    if (appliedFilters.ogakiFactoryOnly) parts.push('大垣工場');
    if (appliedFilters.todayShipOnly) parts.push('本日発送');
    return parts.length ? `検索条件: ${parts.join(', ')}` : '検索条件: なし';
  };

  const renderPrintView = () => {
    const isStockSubTab = activeTab === 'takara' && takaraSubTab === 'stock';
    const sourceItems = isStockSubTab ? [] : items;
    const printItemsFiltered = sourceItems;
    const printCount = Math.min(printItemsFiltered.length, PRINT_MAX_COUNT);

    const printColumns = buildSupplierSharePrintColumns(formatDate, formatNumber);

    return (
      <div className="h-full flex flex-col bg-white print-view-root">
        <div className="sticky top-0 z-20 page-section print:hidden">
          <div className="page-header">
            <div>
              <h1 className="text-xl font-semibold text-slate-900 compact:text-lg">仕入先共有（一覧印刷）</h1>
              <p className="meta-text mt-1">{getPrintSearchSummary()}</p>
              {isStockSubTab && (
                <p className="text-xs text-amber-700 mt-1">在庫入力サブタブは印刷対象外です。発注明細サブタブをご利用ください。</p>
              )}
              {loadError && (
                <p className="text-xs text-red-600 mt-1">{loadError}</p>
              )}
            </div>
            <div className="page-header-actions">
              <button
                type="button"
                onClick={() => {
                  const params = new URLSearchParams(searchParams);
                  params.delete('print_view');
                  setSearchParams(params, { replace: true });
                }}
                className="btn-toolbar text-sm border border-slate-300 rounded-md text-slate-700 bg-white hover:bg-slate-100"
              >
                通常表示に戻る
              </button>
              <button
                type="button"
                onClick={loadInitialItems}
                disabled={loading}
                className="btn-toolbar text-sm border border-slate-300 rounded-md text-slate-700 bg-white hover:bg-slate-100 disabled:opacity-50"
              >
                再試行
              </button>
              <div className="meta-text">
                件数: {printCount}
              </div>
              <button
                type="button"
                onClick={() => {
                  if (isStockSubTab) {
                    alert('在庫入力サブタブは印刷対象外です。発注明細サブタブに切り替えてください。');
                    return;
                  }
                  if (printCount > PRINT_MAX_COUNT) {
                    alert(`印刷上限の${PRINT_MAX_COUNT}件を超えています。条件を絞り込んでください。`);
                    return;
                  }
                  if (printCount === 0) {
                    alert('印刷対象データがありません。');
                    return;
                  }
                  if (printCount > PRINT_WARNING_THRESHOLD) {
                    const ok = window.confirm(
                      `現在の検索条件では ${printCount} 件を印刷しようとしています。本当に印刷してよいですか？`
                    );
                    if (!ok) return;
                  }
                  window.print();
                }}
                className="btn-toolbar bg-slate-700 text-white rounded-md text-sm hover:bg-slate-800"
              >
                印刷実行
              </button>
            </div>
          </div>
        </div>
        <div className={PRINT_PAGE_TITLE_ROW}>
          <h1 className={PRINT_PAGE_TITLE_H1}>仕入先共有一覧</h1>
          <span className={PRINT_PAGE_TITLE_COUNT}>件数: {printCount}</span>
        </div>
        <div className={`${PRINT_SCROLL_CONTAINER} min-h-0`}>
          <table className={PRINT_TABLE}>
            <colgroup>
              {printColumns.map((col) => (
                <col key={col.key} style={{ width: SUPPLIER_SHARE_PRINT_COL_WIDTHS[col.key] || 'auto' }} />
              ))}
            </colgroup>
            <thead className={PRINT_THEAD}>
              <tr className={PRINT_SEARCH_ROW_TR}>
                <th colSpan={printColumns.length} className={PRINT_SEARCH_ROW_TH}>
                  {getPrintSearchSummary()}
                </th>
              </tr>
              <tr>
                {printColumns.map((col) => (
                  <th
                    key={col.key}
                    className={col.key === 'hacchu_amount' ? PRINT_TH_RIGHT : PRINT_TH_LEFT}
                  >
                    {col.header}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {printCount === 0 ? (
                <tr>
                  <td colSpan={printColumns.length} className="px-4 py-6 text-center text-slate-500 text-sm">
                    データがありません
                  </td>
                </tr>
              ) : (
                printItemsFiltered.slice(0, printCount).map((row, idx) => (
                  <tr
                    key={idx}
                    className={`${PRINT_TR_BODY}${isTodayShipShareRow(row) ? ' bg-rose-50' : ''}`}
                  >
                    {printColumns.map((col) => {
                      const tdClass =
                        col.key === 'hacchu_amount' || col.key === 'total_weight' || col.key === 'product_weight'
                          ? PRINT_TD_NUM
                          : SUPPLIER_SHARE_PRINT_COMPOSITE_KEYS.includes(col.key)
                            ? PRINT_TD_COMPOSITE
                            : PRINT_TD_LEFT;
                      return (
                        <td key={col.key} className={tdClass}>
                          {col.render(row)}
                        </td>
                      );
                    })}
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    );
  };

  if (isPrintView) {
    return renderPrintView();
  }

  return (
    <div className="h-full flex flex-col bg-slate-50">
      <div className="page-section">
        <div className="page-header">
          <div className="flex items-baseline gap-3 min-w-0">
            <h1 className="page-title">仕入先共有</h1>
            <Link
              to={(() => {
                const q = buildSupplierShareEditQueryFromViewFilters(filters);
                return q ? `/supplier-share-edit?${q}` : '/supplier-share-edit';
              })()}
              target="_blank"
              rel="noopener noreferrer"
              className="text-sm text-slate-600 underline underline-offset-2 hover:text-slate-900 shrink-0"
            >
              編集画面へ
            </Link>
          </div>
          <div className="page-header-actions">
            <PageSyncMeta groups={['orders']} />
            <button
              onClick={handleRefresh}
              disabled={loading}
              className="flex items-center gap-2 btn-toolbar bg-slate-700 text-white rounded-md hover:bg-slate-800 transition-colors font-medium text-sm disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <svg
                className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`}
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
                />
              </svg>
              更新
            </button>
            <button
              type="button"
              onClick={() => {
                const params = new URLSearchParams(searchParams);
                params.set('print_view', '1');
                setSearchParams(params, { replace: true });
              }}
              className="flex items-center gap-2 btn-toolbar bg-slate-100 text-slate-700 rounded-md hover:bg-slate-200 transition-colors font-medium text-sm border border-slate-300"
            >
              一覧印刷
            </button>
          </div>
        </div>
      </div>

      {/* 発注一覧 / 依頼一覧 */}
      <div className="bg-white border-b border-slate-200 page-section-x">
        <div className="flex space-x-1 pt-2">
          <button
            type="button"
            onClick={() => setListMode('orders')}
            className={`px-4 py-2 text-sm font-medium rounded-t-md ${
              listMode === 'orders' ? 'bg-slate-800 text-white' : 'text-slate-600 hover:text-slate-900'
            }`}
          >
            発注一覧
          </button>
          <button
            type="button"
            onClick={() => setListMode('requests')}
            className={`px-4 py-2 text-sm font-medium rounded-t-md ${
              listMode === 'requests' ? 'bg-slate-800 text-white' : 'text-slate-600 hover:text-slate-900'
            }`}
          >
            依頼一覧
          </button>
        </div>
      </div>

      {listMode === 'requests' ? (
        <div className="page-section-x py-4">
          <ShipRequestListPanel mode="view" onChanged={() => loadInitialItems()} />
        </div>
      ) : (
      <>
      {/* タブ */}
      <div className="bg-white border-b border-slate-200 page-section-x">
        <div className="flex space-x-1">
          <button
            onClick={() => handleTabChange('all')}
            className={`px-0 py-0 border-b-2 transition-colors ${
              activeTab === 'all' ? 'border-slate-900' : 'border-transparent'
            }`}
          >
            <span
              className={`block px-4 py-2 text-sm font-medium transition-colors ${
                activeTab === 'all'
                  ? 'bg-slate-800 text-white rounded-t-md'
                  : 'bg-transparent text-slate-600 hover:text-slate-900'
              }`}
            >
              全体
            </span>
          </button>
          <button
            onClick={() => handleTabChange('kunieda')}
            className={`px-0 py-0 border-b-2 transition-colors ${
              activeTab === 'kunieda' ? 'border-slate-900' : 'border-transparent'
            }`}
          >
            <span
              className={`block px-4 py-2 text-sm font-medium transition-colors ${
                activeTab === 'kunieda'
                  ? 'bg-slate-800 text-white rounded-t-md'
                  : 'bg-transparent text-slate-600 hover:text-slate-900'
              }`}
            >
              国枝工業
            </span>
          </button>
          <button
            onClick={() => handleTabChange('takara')}
            className={`px-0 py-0 border-b-2 transition-colors ${
              activeTab === 'takara' ? 'border-slate-900' : 'border-transparent'
            }`}
          >
            <span
              className={`block px-4 py-2 text-sm font-medium transition-colors ${
                activeTab === 'takara'
                  ? 'bg-slate-800 text-white rounded-t-md'
                  : 'bg-transparent text-slate-600 hover:text-slate-900'
              }`}
            >
              宝機材
            </span>
          </button>
          <button
            onClick={() => handleTabChange('daikure')}
            className={`px-0 py-0 border-b-2 transition-colors ${
              activeTab === 'daikure' ? 'border-slate-900' : 'border-transparent'
            }`}
          >
            <span
              className={`block px-4 py-2 text-sm font-medium transition-colors ${
                activeTab === 'daikure'
                  ? 'bg-slate-800 text-white rounded-t-md'
                  : 'bg-transparent text-slate-600 hover:text-slate-900'
              }`}
            >
              ダイクレ
            </span>
          </button>
          <button
            onClick={() => handleTabChange('others')}
            className={`px-0 py-0 border-b-2 transition-colors ${
              activeTab === 'others' ? 'border-slate-900' : 'border-transparent'
            }`}
          >
            <span
              className={`block px-4 py-2 text-sm font-medium transition-colors ${
                activeTab === 'others'
                  ? 'bg-slate-800 text-white rounded-t-md'
                  : 'bg-transparent text-slate-600 hover:text-slate-900'
              }`}
            >
              その他
            </span>
          </button>
        </div>
        {/* 宝機材タブ用サブタブ */}
        {activeTab === 'takara' && (
          <div className="flex space-x-1 mt-2 border-t border-slate-200 pt-2">
            <button
              onClick={() => {
                setTakaraSubTab('details');
                try {
                  if (typeof window !== 'undefined') {
                    window.localStorage.setItem('supplierShare.takaraSubTab', 'details');
                  }
                } catch (e) {
                  console.warn('Failed to persist takaraSubTab to localStorage', e);
                }
              }}
              className={`px-3 py-1.5 text-xs font-medium rounded ${
                takaraSubTab === 'details'
                  ? 'bg-slate-800 text-white'
                  : 'bg-slate-100 text-slate-700 hover:bg-slate-200'
              }`}
            >
              発注明細
            </button>
            <button
              onClick={() => {
                setTakaraSubTab('stock');
                try {
                  if (typeof window !== 'undefined') {
                    window.localStorage.setItem('supplierShare.takaraSubTab', 'stock');
                  }
                } catch (e) {
                  console.warn('Failed to persist takaraSubTab to localStorage', e);
                }
              }}
              className={`px-3 py-1.5 text-xs font-medium rounded ${
                takaraSubTab === 'stock'
                  ? 'bg-slate-800 text-white'
                  : 'bg-slate-100 text-slate-700 hover:bg-slate-200'
              }`}
            >
              在庫入力
            </button>
          </div>
        )}
      </div>

      {/* フィルタバー */}
      <div className="page-section">
        <form onSubmit={handleFilterSubmit} className="space-y-3">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3">
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">製品名・寸法</label>
              <input
                type="text"
                value={filters.productQuery}
                onChange={(e) => handleFilterChange('productQuery', e.target.value)}
                placeholder="製品名、寸法で検索"
                className="w-full px-3 py-2 border border-slate-300 rounded-md focus:outline-none focus:ring-2 focus:ring-slate-500 text-slate-900"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">得意先名</label>
              <input
                type="text"
                value={filters.customerName}
                onChange={(e) => handleFilterChange('customerName', e.target.value)}
                placeholder="得意先名で検索"
                className="w-full px-3 py-2 border border-slate-300 rounded-md focus:outline-none focus:ring-2 focus:ring-slate-500 text-slate-900"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">発注日（開始）</label>
              <input
                type="date"
                value={filters.startDate}
                onChange={(e) => handleFilterChange('startDate', e.target.value)}
                className="w-full px-3 py-2 border border-slate-300 rounded-md focus:outline-none focus:ring-2 focus:ring-slate-500 text-slate-900"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">発注日（終了）</label>
              <input
                type="date"
                value={filters.endDate}
                onChange={(e) => handleFilterChange('endDate', e.target.value)}
                className="w-full px-3 py-2 border border-slate-300 rounded-md focus:outline-none focus:ring-2 focus:ring-slate-500 text-slate-900"
              />
            </div>
          </div>
          <div className="filter-toolbar">
            <div className="filter-group">
              <span className="text-xs text-slate-500 w-16 flex-shrink-0">発注日:</span>
              <button
                type="button"
                onClick={() => handleFilterChange('hacchuDateFromToday', !filters.hacchuDateFromToday)}
                className={`flex items-center gap-1.5 px-3 py-2 rounded-md transition-colors font-medium text-sm ${
                  filters.hacchuDateFromToday
                    ? 'bg-green-600 text-white hover:bg-green-700'
                    : 'bg-slate-100 text-slate-700 hover:bg-slate-200'
                }`}
                title="発注日が今日以降"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
                </svg>
                <span>今日以降</span>
              </button>
            </div>
            
            {/* 期のフィルタ */}
            <div className="filter-group">
              <span className="text-xs text-slate-500 w-16 flex-shrink-0">期:</span>
              <button
                type="button"
                onClick={() => {
                  const newValue = !filters.currentPeriodOnly;
                  handleFilterChange('currentPeriodOnly', newValue);
                  if (newValue) {
                    // 今期のみをONにしたら、前期以前はOFFにする
                    handleFilterChange('previousPeriodsOnly', false);
                  }
                }}
                className={`flex items-center gap-1.5 px-3 py-2 rounded-md transition-colors font-medium text-sm ${
                  filters.currentPeriodOnly
                    ? 'bg-slate-700 text-white hover:bg-slate-800'
                    : 'bg-slate-100 text-slate-700 hover:bg-slate-200'
                }`}
                title={`今期（${fiscalMeta.current_ki}期）のみ`}
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                <span>今期のみ</span>
              </button>
              <button
                type="button"
                onClick={() => {
                  const newValue = !filters.previousPeriodsOnly;
                  handleFilterChange('previousPeriodsOnly', newValue);
                  if (newValue) {
                    // 前期以前をONにしたら、今期のみはOFFにする
                    handleFilterChange('currentPeriodOnly', false);
                  }
                }}
                className={`flex items-center gap-1.5 px-3 py-2 rounded-md transition-colors font-medium text-sm ${
                  filters.previousPeriodsOnly
                    ? 'bg-slate-700 text-white hover:bg-slate-800'
                    : 'bg-slate-100 text-slate-700 hover:bg-slate-200'
                }`}
                title="前期以前のデータを表示"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                <span>前期以前</span>
              </button>
            </div>
            <div className="filter-group">
              <span className="text-xs text-slate-500 w-16 flex-shrink-0">予定納期:</span>
              <button
                type="button"
                onClick={() => handleFilterChange('supplierResponseDueDateNullOnly', !filters.supplierResponseDueDateNullOnly)}
                className={`flex items-center gap-1.5 px-3 py-2 rounded-md transition-colors font-medium text-sm ${
                  filters.supplierResponseDueDateNullOnly
                    ? 'bg-amber-600 text-white hover:bg-amber-700'
                    : 'bg-slate-100 text-slate-700 hover:bg-slate-200'
                }`}
                title="予定納期が未設定のデータのみ表示"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                <span>未設定</span>
              </button>
              <button
                type="button"
                onClick={() => handleFilterChange('supplierResponseDueDateSetOnly', !filters.supplierResponseDueDateSetOnly)}
                className={`flex items-center gap-1.5 px-3 py-2 rounded-md transition-colors font-medium text-sm ${
                  filters.supplierResponseDueDateSetOnly
                    ? 'bg-green-600 text-white hover:bg-green-700'
                    : 'bg-slate-100 text-slate-700 hover:bg-slate-200'
                }`}
                title="予定納期が設定済のデータのみ表示"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                <span>設定済</span>
              </button>
            </div>
            <div className="filter-group">
              <span className="text-xs text-slate-500 w-16 flex-shrink-0">依頼書:</span>
              <button
                type="button"
                onClick={() => handleFilterChange('shipRequestUnissuedOnly', !filters.shipRequestUnissuedOnly)}
                className={`flex items-center gap-1.5 px-3 py-2 rounded-md transition-colors font-medium text-sm ${
                  filters.shipRequestUnissuedOnly
                    ? 'bg-slate-700 text-white hover:bg-slate-800'
                    : 'bg-slate-100 text-slate-700 hover:bg-slate-200'
                }`}
                title="依頼書未発行のみ表示"
              >
                <span>未発行</span>
              </button>
              <button
                type="button"
                onClick={() => handleFilterChange('shipRequestIssuedOnly', !filters.shipRequestIssuedOnly)}
                className={`flex items-center gap-1.5 px-3 py-2 rounded-md transition-colors font-medium text-sm ${
                  filters.shipRequestIssuedOnly
                    ? 'bg-slate-700 text-white hover:bg-slate-800'
                    : 'bg-slate-100 text-slate-700 hover:bg-slate-200'
                }`}
                title="依頼書発行済（一部含む）のみ表示"
              >
                <span>発行済</span>
              </button>
            </div>
            {/* 受枠フィルタボタン（全体タブのみ、予定納期の右側） */}
            {activeTab === 'all' && (
              <div className="filter-group">
                <span className="text-xs text-slate-500 w-16 flex-shrink-0">製品種別:</span>
                <button
                  type="button"
                  onClick={() => handleFilterChange('jukuwakuFilter', !filters.jukuwakuFilter)}
                  className={`px-4 py-2 rounded-md transition-colors font-medium text-sm ${
                    filters.jukuwakuFilter
                      ? 'bg-slate-700 text-white'
                      : 'bg-slate-100 text-slate-700 hover:bg-slate-200'
                  }`}
                >
                  受枠
                </button>
              </div>
            )}
            {/* 大垣工場フィルタ（宝機材タブのみ） */}
            {activeTab === 'takara' && (
              <div className="filter-group">
                <span className="text-xs text-slate-500 w-16 flex-shrink-0">納入先:</span>
                <button
                  type="button"
                  onClick={() => handleFilterChange('ogakiFactoryOnly', !filters.ogakiFactoryOnly)}
                  className={`flex items-center gap-1.5 px-3 py-2 rounded-md transition-colors font-medium text-sm ${
                    filters.ogakiFactoryOnly
                      ? 'bg-slate-700 text-white hover:bg-slate-800'
                      : 'bg-slate-100 text-slate-700 hover:bg-slate-200'
                  }`}
                  title="納入先に「大垣工場」を含むデータのみ表示"
                >
                  <span>大垣工場</span>
                </button>
              </div>
            )}
            <div className="filter-group">
              <span className="text-xs text-slate-500 w-16 flex-shrink-0">備考:</span>
              <button
                type="button"
                onClick={() => handleFilterChange('todayShipOnly', !filters.todayShipOnly)}
                className={`flex items-center gap-1.5 px-3 py-2 rounded-md transition-colors font-medium text-sm ${
                  filters.todayShipOnly
                    ? 'bg-rose-600 text-white hover:bg-rose-700'
                    : 'bg-slate-100 text-slate-700 hover:bg-slate-200'
                }`}
                title="備考に「本日発送」があり、依頼書の発行日が今日のデータのみ表示"
              >
                <span>本日発送</span>
              </button>
            </div>
            <div className="flex gap-2 ml-auto shrink-0 compact:w-full compact:justify-end">
              <button
                type="submit"
                className="btn-toolbar bg-slate-700 text-white rounded-md hover:bg-slate-800 transition-colors font-medium text-sm"
              >
                検索
              </button>
              <button
                type="button"
                onClick={handleAllFiltersClear}
                className="btn-toolbar bg-slate-100 text-slate-700 rounded-md hover:bg-slate-200 transition-colors font-medium text-sm"
              >
                クリア
              </button>
            </div>
          </div>
        </form>
      </div>

      <div className="flex-1 overflow-hidden flex">
        <div className="flex-1 flex flex-col page-section-x pt-4 pb-2 min-w-0 min-h-0">
          {activeTab === 'takara' && takaraSubTab === 'stock' ? (
            <SupplierStockEditor supplierCode={SUPPLIER_CODES.takara} title="宝機材 在庫入力" readOnly />
          ) : (
            <>
          <div className="mb-2 bg-white rounded-lg shadow-sm border border-slate-200 h-full flex flex-col">
                <div className="flex-1 overflow-auto">
                  <table className="divide-y divide-slate-200" style={{ minWidth: 'max-content', width: '100%' }}>
                    <thead className="bg-slate-50">
                      <tr>
                        {columns.map((col, idx) => (
                          <th
                            key={idx}
                            className="px-4 py-3 text-left text-xs font-semibold text-slate-700 uppercase tracking-wider bg-slate-50 sticky top-0 z-10"
                          >
                            {col.header}
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-slate-100">
                      {items.length === 0 ? (
                        <tr>
                          <td colSpan={columns.length} className="px-4 py-8 text-center text-slate-500">
                            データがありません
                          </td>
                        </tr>
                      ) : (
                        items.map((row, rowIdx) => {
                          const key = getItemKey(row);
                          const isExpanded = expandedRows.has(key);
                          return (
                            <React.Fragment key={key}>
                              <tr
                                onClick={(e) => handleRowClick(row, e)}
                                className={todayShipRowClassName(row)}
                              >
                                {columns.map((col, colIdx) => (
                                  <td key={colIdx} className="px-4 py-3 whitespace-nowrap text-sm text-slate-900">
                                    {col.render ? col.render(row, rowIdx) : row[col.field]}
                                  </td>
                                ))}
                              </tr>
                              {isExpanded && (
                                <tr>
                                  <td colSpan={columns.length} className="px-4 py-4 bg-slate-50">
                                    <div className="bg-white rounded-lg border border-slate-200 p-4">
                                      <h3 className="font-semibold text-sm mb-3 text-slate-900">
                                        分納計画
                                      </h3>
                                      <HacchuSplitPlanInlineEditor
                                        hacchuCode={row.hacchu_code}
                                        hacchuChangecnt={row.hacchu_changecnt}
                                        hacchuDatacode={String(row.hacchu_datacode)}
                                        hacchuAmount={row.hacchu_amount}
                                        shukkaType={row.shukka_type}
                                        hideShipmentFields={false}
                                        readOnly={false}
                                        initialSplitPlans={row.split_plans ?? []}
                                        onDataChange={(splitPlans) => handleSplitPlanDataChange(key, splitPlans)}
                                      />
                                    </div>
                                  </td>
                                </tr>
                              )}
                            </React.Fragment>
                          );
                        })
                      )}
                    </tbody>
                  </table>
                  {hasMore && items.length > 0 && (
                    <div ref={observerTarget} className="h-1 flex-shrink-0 bg-transparent" style={{ minHeight: '1px' }} />
                  )}
                </div>
              </div>
              {!hasMore && items.length > 0 && !loading && (
                <div className="text-center py-3 text-slate-500 text-sm bg-slate-50 border-t border-slate-200 rounded-b-lg flex-shrink-0">
                  すべてのデータを表示しました
                </div>
              )}
            </>
              )}
        </div>
      </div>
      </>
      )}
      {loadingMore && (
        <div className="fixed bottom-6 left-1/2 transform -translate-x-1/2 z-50">
          <div className="flex items-center gap-2 px-4 py-2 bg-slate-700 text-white rounded-lg shadow-lg">
            <svg className="animate-spin h-4 w-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
            </svg>
            <span className="text-sm font-medium">追加データを読み込み中...</span>
          </div>
        </div>
      )}
      <ShipRequestIssueModal
        isOpen={!!requestFormHacchuNo && listMode === 'orders'}
        hacchuNo={requestFormHacchuNo}
        onClose={() => setRequestFormHacchuNo(null)}
        onIssued={() => loadInitialItems()}
      />
      <ShipmentClusterEditModal
        isOpen={!!clusterEditRow}
        anchorRow={clusterEditRow}
        listItems={items}
        onClose={() => setClusterEditRow(null)}
        onSaved={handleClusterEditSaved}
      />
    </div>
  );
};

export default SupplierSharePage;
